(function () {
  'use strict';

  const SELECTOR_NAV = '[data-slider-logos]';
  const SELECTOR_TEXT = '[data-slider-text]';
  const SELECTOR_SLIDE = '[data-slide]';
  const SELECTOR_FIRST_GROUP_SLIDES = '.logos__marquee-group:not(.logos__marquee-group--clone) [data-slide]';
  const CLASS_SELECTED = 'is-selected';
  const CLASS_INITIALIZED = 'is-initialized';
  const CLASS_FLICKITY = 'flickity-enabled';
  const ATTR_SLIDE = 'data-slide';
  const ATTR_INDEX = 'data-slide-index';

  if (customElements.get('logos-component')) return;

  customElements.define(
    'logos-component',
    class extends HTMLElement {
      constructor() {
        super();
        this.setSlideshowNavStateOnResize = () => this.setSlideshowNavState();
        this.flkty = null;
        this.flktyNav = null;
        this.marqueeQuoteRaf = null;
        this.marqueeActiveIndex = null;
        this.hoverQuoteLock = null;
        this.logoOffsetByIndex = new Map();
        this.marqueeQuoteAlignPx = 6;
      }

      connectedCallback() {
        this.stripMode = this.dataset.stripMode || 'carousel';
        this.slideshowNav = this.querySelector(SELECTOR_NAV);
        this.slideshowText = this.querySelector(SELECTOR_TEXT);
        this.logoSlides = this.getLogoSlideElements();
        this.logoSlidesWidth = this.getSlidesWidth();
        this.bindEvents();

        this.initSlideshowText();

        if (this.stripMode === 'marquee') {
          this.initMarqueeQuoteSync();
        } else {
          this.initSlideshowNav();
        }
      }

      getLogoSlideElements() {
        if (!this.slideshowNav) return [];
        if (this.stripMode === 'marquee') {
          return this.slideshowNav.querySelectorAll(SELECTOR_SLIDE);
        }
        const firstGroupSlides = this.slideshowNav.querySelectorAll(SELECTOR_FIRST_GROUP_SLIDES);
        if (firstGroupSlides.length) return firstGroupSlides;
        return this.slideshowNav.querySelectorAll(SELECTOR_SLIDE);
      }

      getSlidesWidth() {
        return 200 * this.logoSlides.length;
      }

      initSlideshowText() {
        if (!this.slideshowText) return;

        this.flkty = new window.theme.FlickityFade(this.slideshowText, {
          fade: true,
          autoPlay: false,
          prevNextButtons: false,
          cellAlign: 'left',
          contain: true,
          pageDots: false,
          wrapAround: false,
          selectedAttraction: 0.2,
          friction: 0.6,
          draggable: false,
          accessibility: false,
          on: {
            ready: () => this.sliderAccessibility(),
            change: () => this.sliderAccessibility(),
          },
        });
      }

      sliderAccessibility() {
        if (!this.slideshowText) return;

        const focusables = this.slideshowText.querySelectorAll(`${SELECTOR_SLIDE} a, ${SELECTOR_SLIDE} button`);
        focusables.forEach((el) => {
          const cell = el.closest(SELECTOR_SLIDE);
          if (!cell) return;
          const tabIndex = cell.classList.contains(CLASS_SELECTED) ? 0 : -1;
          el.setAttribute('tabindex', tabIndex);
        });
      }

      setLogoSelectionByIndex(index) {
        this.logoSlides.forEach((s) => {
          const slideIndex = parseInt(s.getAttribute(ATTR_INDEX), 10);
          s.classList.toggle(CLASS_SELECTED, slideIndex === index);
        });
      }

      selectQuoteByIndex(index, { fromHover = false } = {}) {
        if (Number.isNaN(index)) return;
        if (!fromHover && this.hoverQuoteLock != null) return;
        if (this.marqueeActiveIndex === index && !fromHover) return;

        this.marqueeActiveIndex = index;
        this.setLogoSelectionByIndex(index);
        if (this.flkty) this.flkty.select(index);
      }

      getPageCenterX() {
        const pageWidth =
          window.theme && typeof window.theme.getWindowWidth === 'function'
            ? window.theme.getWindowWidth()
            : document.documentElement.clientWidth;
        return pageWidth / 2;
      }

      syncMarqueeQuoteToCenter() {
        if (this.hoverQuoteLock != null || !this.slideshowNav || !this.logoSlides.length) return;

        const pageCenterX = this.getPageCenterX();
        const pageWidth =
          window.theme && typeof window.theme.getWindowWidth === 'function'
            ? window.theme.getWindowWidth()
            : document.documentElement.clientWidth;
        const nearestByIndex = new Map();
        const alignPx = this.marqueeQuoteAlignPx;

        this.logoSlides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          if (slideRect.right < 0 || slideRect.left > pageWidth) return;

          const slideCenterX = slideRect.left + slideRect.width / 2;
          const offset = slideCenterX - pageCenterX;
          const absDist = Math.abs(offset);
          const index = parseInt(slide.getAttribute(ATTR_INDEX), 10);
          if (Number.isNaN(index)) return;

          const existing = nearestByIndex.get(index);
          if (!existing || absDist < existing.absDist) {
            nearestByIndex.set(index, { offset, absDist, index });
          }
        });

        let crossedIndex = null;
        let alignedIndex = null;
        let alignedDist = Infinity;

        nearestByIndex.forEach(({ offset, absDist, index }) => {
          const prevOffset = this.logoOffsetByIndex.get(index);

          if (
            prevOffset != null &&
            prevOffset !== 0 &&
            offset !== 0 &&
            Math.sign(prevOffset) !== Math.sign(offset) &&
            absDist <= alignPx * 2
          ) {
            crossedIndex = index;
          }

          if (absDist < alignedDist) {
            alignedDist = absDist;
            alignedIndex = index;
          }

          this.logoOffsetByIndex.set(index, offset);
        });

        if (crossedIndex != null) {
          this.selectQuoteByIndex(crossedIndex);
          return;
        }

        if (alignedIndex != null && alignedDist <= alignPx) {
          this.selectQuoteByIndex(alignedIndex);
        }
      }

      startMarqueeQuoteLoop() {
        const tick = () => {
          if (this.stripMode === 'marquee') this.syncMarqueeQuoteToCenter();
          this.marqueeQuoteRaf = requestAnimationFrame(tick);
        };
        this.marqueeQuoteRaf = requestAnimationFrame(tick);
      }

      initMarqueeQuoteSync() {
        if (!this.slideshowText || !this.flkty || !this.logoSlides.length) return;

        this.hoverQuoteLock = null;

        this.logoSlides.forEach((slide) => {
          const activateFromPointer = () => {
            const index = parseInt(slide.getAttribute(ATTR_INDEX), 10);
            this.hoverQuoteLock = index;
            this.selectQuoteByIndex(index, { fromHover: true });
          };

          slide.addEventListener('mouseenter', activateFromPointer);
          slide.addEventListener('focusin', activateFromPointer);
        });

        this.slideshowNav.addEventListener('mouseleave', () => {
          this.hoverQuoteLock = null;
          this.marqueeActiveIndex = null;
          this.syncMarqueeQuoteToCenter();
        });

        this.slideshowNav.addEventListener('focusout', (event) => {
          if (this.slideshowNav.contains(event.relatedTarget)) return;
          this.hoverQuoteLock = null;
          this.marqueeActiveIndex = null;
          this.syncMarqueeQuoteToCenter();
        });

        this.selectQuoteByIndex(0);
        this.startMarqueeQuoteLoop();
      }

      bindCarouselHoverQuote() {
        if (!this.slideshowNav || !this.logoSlides.length) return;

        const activateFromPointer = (slide) => {
          const index = parseInt(slide.getAttribute(ATTR_INDEX), 10);
          this.hoverQuoteLock = index;
          if (this.flkty) this.flkty.select(index);
          this.setLogoSelectionByIndex(index);
        };

        this.logoSlides.forEach((slide) => {
          slide.addEventListener('mouseenter', () => activateFromPointer(slide));
          slide.addEventListener('focusin', () => activateFromPointer(slide));
        });

        this.slideshowNav.addEventListener('mouseleave', () => {
          this.hoverQuoteLock = null;
        });

        this.slideshowNav.addEventListener('focusout', (event) => {
          if (this.slideshowNav.contains(event.relatedTarget)) return;
          this.hoverQuoteLock = null;
        });
      }

      initSlideshowNav() {
        if (!this.slideshowNav || !this.logoSlides.length) return;

        this.bindCarouselHoverQuote();

        this.logoSlides.forEach((slide) => {
          slide.addEventListener('click', () => {
            const index = parseInt(slide.getAttribute(ATTR_INDEX), 10);
            const navHasFlickity = this.slideshowNav.classList.contains(CLASS_FLICKITY);

            if (this.flkty) this.flkty.select(index);

            if (navHasFlickity) {
              if (this.flktyNav) {
                this.flktyNav.select(index);
                if (!this.slideshowNav.classList.contains(CLASS_INITIALIZED)) {
                  this.flktyNav.playPlayer();
                }
              }
            } else {
              const prev = this.slideshowNav.querySelector(`.${CLASS_SELECTED}`);
              if (prev) prev.classList.remove(CLASS_SELECTED);
              slide.classList.add(CLASS_SELECTED);
            }
          });
        });

        this.setSlideshowNavState();
        document.addEventListener('theme:resize', this.setSlideshowNavStateOnResize);
      }

      setSlideshowNavState() {
        if (this.stripMode === 'marquee') return;
        if (!this.slideshowNav) return;

        const navHasFlickity = this.slideshowNav.classList.contains(CLASS_FLICKITY);

        if (this.logoSlidesWidth > window.theme.getWindowWidth()) {
          if (!navHasFlickity) {
            this.slideshowNav.classList.add(CLASS_INITIALIZED);
            const selected = this.slideshowNav.querySelector(`.${CLASS_SELECTED}`);
            if (selected) selected.classList.remove(CLASS_SELECTED);
            if (this.logoSlides[0]) this.logoSlides[0].classList.add(CLASS_SELECTED);

            if (!this.flktyNav) {
              this.flktyNav = new window.theme.Flickity(this.slideshowNav, {
                autoPlay: 4000,
                prevNextButtons: false,
                contain: false,
                pageDots: false,
                wrapAround: true,
                watchCSS: true,
                selectedAttraction: 0.05,
                friction: 0.8,
                initialIndex: 0,
              });

              this.flktyNav.on('deactivate', () => {
                const firstSlide = this.slideshowNav.querySelector(SELECTOR_SLIDE);
                if (firstSlide) firstSlide.classList.add(CLASS_SELECTED);
                if (this.flkty) this.flkty.select(0);
              });

              if (this.flkty) {
                this.flkty.select(0);
                this.flktyNav.on('change', (idx) => {
                  if (this.hoverQuoteLock != null) return;
                  this.flkty.select(idx);
                  this.setLogoSelectionByIndex(idx);
                });
              }
            }
          }
        } else if (navHasFlickity) {
          this.slideshowNav.classList.remove(CLASS_INITIALIZED);
        }
      }

      onBlockSelect(evt) {
        if (!this.slideshowNav) return;

        const blockId = evt && (evt.detail?.blockId ?? evt.blockId);
        if (!blockId) return;

        const slide = this.slideshowNav.querySelector(`[${ATTR_SLIDE}="${blockId}"]`);
        if (!slide) return;

        const index = parseInt(slide.getAttribute(ATTR_INDEX), 10);

        if (this.stripMode === 'marquee') {
          const target =
            slide.closest('.logos__marquee-group--clone') != null
              ? this.slideshowNav.querySelector(
                  `.logos__marquee-group:not(.logos__marquee-group--clone) [${ATTR_SLIDE}="${blockId}"]`
                )
              : slide;
          if (!target) return;
          this.hoverQuoteLock = index;
          this.selectQuoteByIndex(index, { fromHover: true });
          return;
        }

        if (this.slideshowNav.classList.contains(CLASS_FLICKITY)) {
          if (this.flktyNav) {
            this.flktyNav.select(index);
            this.flktyNav.stopPlayer();
            this.slideshowNav.classList.add(CLASS_SELECTED);
          }
        } else {
          slide.dispatchEvent(new Event('click'));
        }
      }

      onBlockDeselect() {
        if (!this.slideshowNav) return;
        if (this.stripMode === 'marquee') return;

        if (this.slideshowNav.classList.contains(CLASS_FLICKITY)) {
          if (this.flktyNav) this.flktyNav.playPlayer();
          this.slideshowNav.classList.remove(CLASS_SELECTED);
        }
      }

      bindEvents() {
        this.addEventListener('theme:slider-logos:select', (e) => this.onBlockSelect(e.detail.evt));
        this.addEventListener('theme:slider-logos:deselect', () => this.onBlockDeselect());
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.setSlideshowNavStateOnResize);
        if (this.marqueeQuoteRaf) cancelAnimationFrame(this.marqueeQuoteRaf);
      }
    }
  );
})();
